import { Button, Link } from "@mui/material";
import { useMemo, useState } from "react";
import { useTranslations } from "use-intl";

import { DrawerTopBar } from "@/components/Drawer/DrawerTopBar";
import { SwipeableDrawer } from "@/components/Drawer/SwipeableDrawer";
import { usePaymentContext } from "@/contexts/PaymentContext";
import { RoomGalleryButton } from "@/features/hotels/components/RoomSelection/HotelGalleryButton";
import { RoomDetails } from "@/features/hotels/components/RoomSelection/RoomDetails";
import {
  MealFeature,
  RoomFeature,
} from "@/features/hotels/components/RoomSelection/RoomFeature";
import type {
  HotelBookingRoomOption,
  HotelData,
  RoomImage,
} from "@/features/hotels/types/types";
import { includeBnplFee, calculateMonthlyInstallment } from "@/utils/bnpl-fee";
import { formatCurrency } from "@/utils/currency";
import { Feature, isFeatureEnabled } from "@/utils/featureFlags";

type RoomGroupType = {
  name: string;
  thumbnailImage?: RoomImage;
  images?: RoomImage[];
  options: HotelBookingRoomOption[];
};

type Props = {
  hotel: HotelData;
  rooms: HotelBookingRoomOption[];
  guestCount: number;
  bookingNights: number;
  onRoomSelected: (option: HotelBookingRoomOption[]) => void;
  onRoomGalleryClick: (room: HotelBookingRoomOption) => void;
};

export const HotelRoomSelection = ({
  hotel,
  rooms,
  guestCount,
  bookingNights,
  onRoomSelected,
  onRoomGalleryClick,
}: Props) => {
  const t = useTranslations();
  const [selectedRoomDetails, setSelectedRoomDetails] =
    useState<HotelBookingRoomOption | null>(null);
  const { maxAvailableInstallments, isEligible, isEligibilityLoading } =
    usePaymentContext();
  const isBnplFeatureEnabled =
    isFeatureEnabled(Feature.Bnpl) && Boolean(isEligible);

  // Group same rooms with different options (board, refundable, etc.) under the same room name
  const groupedRooms = useMemo(
    () =>
      Object.values(
        rooms.reduce(
          (acc, room) => {
            const key = room.name.toLowerCase();

            if (!acc[key]) {
              acc[key] = {
                name: room.name,
                thumbnailImage: room.images?.[0],
                images: room.images,
                options: [],
              };
            }
            acc[key].options.push(room);

            return acc;
          },
          {} as Record<string, RoomGroupType>,
        ),
      ),
    [rooms],
  );

  return (
    <>
      <ul className="space-y-3">
        {groupedRooms.map((roomGroup) => (
          <li
            key={roomGroup.name}
            className="rounded-3xl bg-white pb-4 text-text-primary"
          >
            <div className="mb-3 flex p-1">
              {roomGroup.thumbnailImage ? (
                <RoomGalleryButton
                  onClick={() => onRoomGalleryClick(roomGroup.options[0])}
                  thumbnailImage={roomGroup.thumbnailImage}
                  imageCount={roomGroup.images?.length || 0}
                />
              ) : null}

              <div className="overflow-hidden p-3">
                <div className="mb-1 line-clamp-2 text-lg font-medium">
                  {roomGroup.name}
                </div>

                <div className="mb-2 text-sm text-text-tertiary">
                  {t("hotels.guestsAndNights", {
                    guests: guestCount,
                    nights: bookingNights,
                  })}
                </div>

                <Link
                  onClick={() => setSelectedRoomDetails(roomGroup.options[0])}
                  component="button"
                  sx={{
                    fontSize: "var(--text-sm)",
                    fontWeight: "var(--font-weight-medium)",
                    color: "var(--color-surface-accent-strong)",
                  }}
                >
                  {t("hotels.seeRoomDetails")}
                </Link>
              </div>
            </div>

            <ul className="flex w-full snap-x snap-mandatory flex-nowrap gap-4 overflow-x-auto px-3">
              {roomGroup.options.map((option) => (
                <li
                  key={option.roomToken}
                  className="flex w-55 shrink-0 snap-center flex-col rounded-2xl border border-stroke-neutral-soft p-4"
                >
                  <ul className="mb-6 space-y-2">
                    <li>
                      {option.tags.includes("Non-refundable") ? (
                        <RoomFeature
                          label={t("hotels.nonRefundable")}
                          isIncluded={false}
                        />
                      ) : (
                        <RoomFeature
                          label={t("hotels.refundable")}
                          isIncluded
                        />
                      )}
                    </li>
                    <li>
                      <MealFeature board={option.boardBasis} />
                    </li>
                  </ul>

                  <div className="mt-auto mb-4">
                    <div className="w-full overflow-hidden text-base font-bold overflow-ellipsis">
                      {formatCurrency(
                        option.price?.value || 0,
                        option.price?.currency,
                      )}
                    </div>
                    {isBnplFeatureEnabled && (
                      <div className="overflow-hidden text-sm font-medium overflow-ellipsis">
                        {t("payment.orMonthly", {
                          price: formatCurrency(
                            calculateMonthlyInstallment(
                              includeBnplFee(option.price?.value || 0),
                              maxAvailableInstallments,
                            ),
                            option.price?.currency,
                          ),
                        })}
                      </div>
                    )}
                    <div className="mt-1 text-xs text-text-tertiary">
                      {t("payment.totalWithTaxes")}
                    </div>
                  </div>

                  <Button
                    type="button"
                    variant="contained"
                    disableElevation
                    fullWidth
                    onClick={() => {
                      onRoomSelected([option]);
                    }}
                    loading={isEligibilityLoading}
                  >
                    {t("select")}
                  </Button>
                </li>
              ))}
            </ul>
          </li>
        ))}
      </ul>

      {selectedRoomDetails ? (
        <SwipeableDrawer
          open
          hasFullHeight
          heightOffset={24}
          onClose={() => setSelectedRoomDetails(null)}
          backgroundColor="var(--color-surface-background)"
        >
          <DrawerTopBar
            onCloseClick={() => setSelectedRoomDetails(null)}
            title={t("hotels.roomDetails")}
          />
          <RoomDetails option={selectedRoomDetails} hotelName={hotel.name} />
        </SwipeableDrawer>
      ) : null}
    </>
  );
};
